import { Components, Theme } from '@mui/material'

declare module '@mui/material/IconButton' {
  interface IconButtonOwnProps {
    // IconButton doesn't have variants in MUI, so adding them is different
    variant?: 'futureTheme.contained' | 'futureTheme.ghost'
  }
}
const loadingIndicatorSelector = '& .MuiIconButton-loadingIndicator'

export const MuiIconButton: Components<Omit<Theme, 'components'>>['MuiIconButton'] = {
  defaultProps: {
    disableRipple: true,
    variant: 'futureTheme.ghost',
    color: 'secondary',
  },
  styleOverrides: {
    root: {
      '&.Mui-disabled': {
        opacity: 0.4,
      },
      // Hide whatever the button renders while loading, not just SvgIcons
      '&.MuiIconButton-loading > *:not(.MuiIconButton-loadingWrapper)': {
        opacity: 0,
      },
      // Icon always the same size
      '& .MuiSvgIcon-root': {
        fontSize: '1.25rem',
      },
    },
  },
  variants: [
    {
      props: { variant: 'futureTheme.contained' },
      style: {},
    },
    {
      props: { variant: 'futureTheme.ghost' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.invisible,
      }),
    },
    // Colour
    {
      props: { color: 'primary', variant: 'futureTheme.contained' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.brand.default,
        color: theme.palette.futureTheme.text.brand.onBrand,
        '&:hover': {
          backgroundColor: theme.palette.futureTheme.bg.brand.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          backgroundColor: theme.palette.futureTheme.bg.brand.default,
          color: theme.palette.futureTheme.text.brand.onBrand,
        },
      }),
    },
    {
      props: { color: 'primary', variant: 'futureTheme.ghost' },
      style: ({ theme }) => ({
        color: theme.palette.futureTheme.text.brand.default,
        '&:hover': {
          backgroundColor: theme.palette.futureTheme.bg.brand.muted,
          color: theme.palette.futureTheme.text.brand.onBrandMuted,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.futureTheme.text.brand.default,
        },
      }),
    },
    {
      props: { color: 'secondary', variant: 'futureTheme.contained' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
        borderStyle: 'solid',
        borderWidth: theme.futureTheme.strokeWidth.default,
        borderColor: theme.palette.futureTheme.border.subtle,
        color: theme.palette.futureTheme.text.muted,
        boxShadow: theme.futureTheme.shadows.buttonDrop,
        '&:hover': {
          borderColor: theme.palette.futureTheme.border.strong,
          boxShadow: [theme.futureTheme.shadows.buttonInner, theme.futureTheme.shadows.buttonDrop].join(', '),
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          backgroundColor: theme.palette.futureTheme.bg.surface.elevated,
          color: theme.palette.futureTheme.text.default,
          boxShadow: [theme.futureTheme.shadows.buttonInner, theme.futureTheme.shadows.buttonDrop].join(', '),
        },
      }),
    },
    {
      props: { color: 'secondary', variant: 'futureTheme.ghost' },
      style: ({ theme }) => ({
        color: theme.palette.futureTheme.text.muted,
        '&:hover': {
          color: theme.palette.futureTheme.text.default,
          backgroundColor: theme.palette.futureTheme.bg.interactive.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.futureTheme.text.default,
        },
      }),
    },
    {
      props: { color: 'error', variant: 'futureTheme.contained' },
      style: ({ theme }) => ({
        backgroundColor: theme.palette.futureTheme.bg.danger.default,
        color: theme.palette.futureTheme.text.danger.onDanger,
        '&:hover': {
          backgroundColor: theme.palette.futureTheme.bg.danger.hover,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          backgroundColor: theme.palette.futureTheme.bg.danger.default,
          color: theme.palette.futureTheme.text.danger.onDanger,
        },
      }),
    },
    {
      props: { color: 'error', variant: 'futureTheme.ghost' },
      style: ({ theme }) => ({
        color: theme.palette.futureTheme.text.danger.default,
        '&:hover': {
          color: theme.palette.futureTheme.text.danger.onDangerMuted,
          backgroundColor: theme.palette.futureTheme.bg.danger.muted,
        },
        [`&.Mui-disabled, ${loadingIndicatorSelector}`]: {
          color: theme.palette.futureTheme.text.danger.onDangerMuted,
        },
      }),
    },
    // Size
    {
      props: { size: 'large' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['futureTheme.radius-lg'],
        padding: theme.spacing(2.5),
      }),
    },
    {
      props: { size: 'medium' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['futureTheme.radius-lg'],
        padding: theme.spacing(2),
      }),
    },
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        borderRadius: theme.shape['futureTheme.radius-md'],
        padding: theme.spacing(1),
      }),
    },
  ],
}
